import React from "react";
import {PlatformApp} from '@platform/cj-platform-navigation'
import Content from "./components/Content/Content";
import {UserContextProvider} from "./context/UserContextProvider";
import {Filters} from "./components/Filters/Filters";
import CampaignsProvider from "./components/CampaignsContext/CampaignsProvider";
import * as util from "./utils/util";
import {
    authenticatedFetch,
    debouncedFetch,
    fetchPublishersBySearchTerm,
    getApolloClient,
    getPublishers,
    getUser,
    sortAndRemoveDuplicates
} from "./utils/services";
import {ApolloProvider} from "@apollo/client";
import {Provider} from "react-redux";
import {DateFilter} from "./components/DateFilter/DateFilter";
import * as appConstant from "./constants/appConstant";
import {initializePendo} from "./components/Pendo/pendo";
import PlacementModalDialog from "./components/CreateRecord/PlacementModalDialog";
import {combineReducers, createStore} from "redux";
import {reducer as visualStackReducer} from "@cjdev-internal/visual-stack-redux";
import PublisherSelectionDropdown from "./components/FilterNav/PublisherSelectionDropdown";
import AccountSelectionDropdown from "./components/FilterNav/AccountSelectionDropdown";
import Dimensions from "./components/Dimensions/Dimensions";
import MediaTrackerAppNewNav from "./MediaTrackerAppNewNav";
import ActionButtons from "./components/ActionButtons/ActionButtons";
import getCampaignsRoute from "./navigation/getCampaignsRoute";
import generateRoute from "./navigation/generateRoute";
import RemoveRecordModal from "./components/RemoveRecordModal/RemoveRecordModal";
import PlacementDialogDuplicateSubmitAction from "./components/CreateRecord/PlacementDialogDuplicateSubmitAction";
import PlacementDialogSubmitEditAction from "./components/CreateRecord/PlacementDialogSubmitEditAction";
import DecideRoute from "./components/Routes/DecideRoute";
import {Box} from "@cjdev-internal/visual-stack-x/Box";
import {LoadingAnimation} from "@cjdev-internal/visual-stack-x/LoadingAnimation";
import CampaignsDataTable from "./components/DataTable/CampaignsDataTable";
import openModalDialogProvider from "./components/CreateRecord/OpenModalDialogProvider";
import {useModal} from "@cjdev-internal/visual-stack-x/Modal";
import {useAlert} from "@cjdev-internal/visual-stack-x/Alert";
import AddPlacementButton from "./components/AddPlacementButton/AddPlacementButton";
import PlacementDialogSubmitAction from "./components/CreateRecord/PlacementDialogSubmitAction";
import placementDialogSubmitAction from "./components/CreateRecord/PlacementDialogSubmitAction";
import SearchPanel from "./components/DataTable/SearchPanel";
import getPlatformDomains from "./navigation/getPlatformDomains";
import BulkUpload from "./components/BulkUpload/BulkUpload";
import BulkUploadPageContent from "./components/BulkUpload/BulkUploadPageContent";
import {
    BULK_UPLOAD_DATATABLE_COLUMNS,
    BULK_UPLOAD_DATATABLE_COLUMNS_WITH_VALIDATION, SAVE_PROCESSING_MODAL, SAVE_SUMMARY_MODAL, UPLOAD_NOTIFICATION_MODAL
} from "./components/BulkUpload/BulkUploadConstant";
import BulkUploadDataTable from "./components/BulkUpload/BulkUploadDataTable";
import {Row} from "@cjdev-internal/visual-stack-x/Row";
import bulkUploadValidationProvider from "./components/BulkUpload/Validation/bulkUploadValidationProvider";
import dataTypeValidator from "./components/BulkUpload/Validation/dataTypeValidator";
import pidValidation from "./components/BulkUpload/Validation/pidValidation";
import {bulkUploadErrorsFormatter, bulkUploadWarningFormatter} from "./utils/formatFunctions";
import getYOYDetails from "./components/BulkUpload/Validation/getYOYDetails";
import getBaselineDetails from "./components/BulkUpload/Validation/getBaselineDetails";
import PlacementDataParser from "./components/BulkUpload/PlacementDataParser";
import {BulkUploadContextProvider} from "./components/BulkUploadContext/BulkUploadContext";
import {ErrorPage} from "@cjdev-internal/visual-stack-x/ErrorPage";
import {DATATABLE_COLUMNS} from "./components/DataTable/DataTableConstant";
import UploadNotification from "./components/BulkUpload/modalContent/UploadNotification";
import DownloadContentIcon from "./components/BulkUpload/DownloadContentIcon";
import SavePlacementProcessing from "./components/BulkUpload/modalContent/SavePlacementProcessing";
import SavePlacementSummary from "./components/BulkUpload/modalContent/SavePlacementSummary";
import {BulkUploadAndDownloadDropdown} from "./components/DropdownMenu/BulkUploadAndDownloadDropdown";

const CampaignsPlatformNavigation = () => {
    const [modalMount, openModal, closeModal] = useModal();
    const [uploadModalMount, uploadOpenModal, uploadCloseModal] = useModal();
    const [alertMount, openAlert, closeAlert] = useAlert();
    const [uploadAlertMount, uploadOpenAlert, uploadCloseAlert] = useAlert();

    const modalConfiguration = {
        [appConstant.PLACEMENT_MODE_CREATE]: {
            ModalContent: PlacementModalDialog,
            props: {
                takeAction: PlacementDialogSubmitAction,
                placementModalTitle: appConstant.CREATE_RECORD_TITLE,
                modeOfPlacement: appConstant.PLACEMENT_MODE_CREATE,
            }
        },
        [appConstant.PLACEMENT_MODE_EDIT]: {
            ModalContent: PlacementModalDialog,
            props: {
                takeAction: PlacementDialogSubmitEditAction,
                placementModalTitle: appConstant.EDIT_RECORD_TITLE,
                modeOfPlacement: appConstant.PLACEMENT_MODE_EDIT,
            }
        },
        [appConstant.PLACEMENT_MODE_DUPLICATE]: {
            ModalContent: PlacementModalDialog,
            props: {
                takeAction: PlacementDialogDuplicateSubmitAction,
                placementModalTitle: appConstant.DUPLICATE_RECORD_TITLE,
                modeOfPlacement: appConstant.PLACEMENT_MODE_DUPLICATE,
            }
        },
        [appConstant.PLACEMENT_MODE_REMOVE]: {
            ModalContent: RemoveRecordModal
        },
        [UPLOAD_NOTIFICATION_MODAL]: {
            isBulkUploadPopup: true,
            props: {},
            ModalContent: UploadNotification
        },
        [SAVE_PROCESSING_MODAL]: {
            isBulkUploadPopup: true,
            ModalContent: SavePlacementProcessing
        },
        [SAVE_SUMMARY_MODAL]: {
            isBulkUploadPopup: true,
            ModalContent: SavePlacementSummary
        }
    };
    const client = getApolloClient();
    const openModalDialog = openModalDialogProvider(openModal, closeModal, openAlert, closeAlert, modalConfiguration, util.getBearerToken, util.deSanitizeObject);
    const uploadModalDialog = openModalDialogProvider(uploadOpenModal, uploadCloseModal, {}, {}, modalConfiguration, {}, util.deSanitizeObject);

    const CampaignsDataTableWrapper = CampaignsDataTable(DATATABLE_COLUMNS, openModalDialog, SearchPanel);

    const BulkUploadAndDownloadWrapper = () => <BulkUploadAndDownloadDropdown util={util}/>;

    const MediaTrackerAppWrapper = () => (
        <MediaTrackerAppNewNav
            getUser={getUser}
            CampaignsDataTable={CampaignsDataTableWrapper}
            getCompaniesDetails={util.getCompaniesDetails}
            getNonEmptyVariables={util.getNonEmptyVariables}
            initializePendo={initializePendo}
        />
    );

    const ContentWrapper = ({user}) => <Content user={user} MediaTrackerApp={MediaTrackerAppWrapper}/>;

    const downloadIconWrapper = ({api}) => <DownloadContentIcon api={api}
                                                                downloadTemplateAndFailedPlacement={util.downloadTemplateAndFailedPlacement}/>

    const BulkUploadDataTableWrapper = BulkUploadDataTable(BULK_UPLOAD_DATATABLE_COLUMNS_WITH_VALIDATION, downloadIconWrapper);
    const typeValidation = dataTypeValidator(BULK_UPLOAD_DATATABLE_COLUMNS);
    const pidValidationWrapper = pidValidation(client);
    const yoyDetails = getYOYDetails(client);
    const baselineDetails = getBaselineDetails(client);
    const bulkUploadValidation = bulkUploadValidationProvider(util.formValidate, typeValidation, pidValidationWrapper, yoyDetails, baselineDetails, bulkUploadErrorsFormatter, bulkUploadWarningFormatter);
    const BulkUploadPageContentWrapper = () => (
        <BulkUploadContextProvider>
            <BulkUpload placementSubmitAction={placementDialogSubmitAction} modalMount={uploadModalMount}
                        openModalDialog={uploadModalDialog} closeModal={uploadCloseModal} openAlert={uploadOpenAlert}
                        closeAlert={uploadCloseAlert} alertMount={uploadAlertMount}>
                <BulkUploadPageContent BulkUploadDataTableWrapper={BulkUploadDataTableWrapper}
                                       getTextAfterLastDot={util.getTextAfterLastDot}
                                       PlacementDataParser={PlacementDataParser}
                                       bulkUploadValidation={bulkUploadValidation}
                                       getCompaniesWithIdName={util.getCompaniesWithIdName}
                                       hasValue={util.hasValue}
                                       closeModal={uploadCloseModal}
                                       openModalDialog={uploadModalDialog}
                                       downloadExcel={util.downloadTemplateAndFailedPlacement}
                />
            </BulkUpload>
        </BulkUploadContextProvider>
    );
    const BulkUploadWrapper = ({user}) => <Content user={user} MediaTrackerApp={BulkUploadPageContentWrapper}/>;

    const _fetchPublishersBySearchTerm = fetchPublishersBySearchTerm(authenticatedFetch, sortAndRemoveDuplicates);

    const _getPublishers = getPublishers(_fetchPublishersBySearchTerm);

    const PublisherSelectionDropdownWrapper = PublisherSelectionDropdown(_getPublishers, debouncedFetch);

    const FiltersWrapper = () => <Filters
        PublisherSelectionDropdown={PublisherSelectionDropdownWrapper}
        AccountSelectionDropdown={AccountSelectionDropdown}/>;

    const DimensionsWrapper = () => <Dimensions DateFilter={DateFilter}/>

    const AddPlacementButtonWrapper = () => (
        <Row gap={"medium"}>
            <AddPlacementButton openModalDialog={openModalDialog}/>
        </Row>)

    const ActionButtonsWrapper = () => <ActionButtons CreateRecordModal={AddPlacementButtonWrapper}
                                                      BulkUploadAndDownloadDropDown={BulkUploadAndDownloadWrapper}/>

    const components = {
        Filters: FiltersWrapper,
        Dimensions: DimensionsWrapper,
        Buttons: ActionButtonsWrapper,
        ContentWrapper: ContentWrapper,
        BulkUploadWrapper: BulkUploadWrapper
    };


    const MountPointWrapper = () =>
        (<>
            {modalMount}
            {alertMount}
        </>)

    const reducer = combineReducers({
        visualStack: visualStackReducer,
    });
    const store = createStore(reducer);
    const LoadingComponent = () => (
        <Box direction="row" justify="center"
             className="loadingAnimationBox"><LoadingAnimation/>
        </Box>
    );

    const LoadingWrapper = {
        ContentWrapper: ({user}) => <Content user={user} MediaTrackerApp={LoadingComponent}/>,
        BulkUploadWrapper: ({user}) => <Content user={user} MediaTrackerApp={LoadingComponent}/>
    }

    const UnauthorizedComponent = () => (
        <ErrorPage autoPosition={true}
                   errorCode={403}/>
    )

    const UnauthorizedWrapper = {
        ContentWrapper: ({user}) => <Content user={user} MediaTrackerApp={UnauthorizedComponent}/>,
        BulkUploadWrapper: ({user}) => <Content user={user} MediaTrackerApp={UnauthorizedComponent}/>
    }
    const CampaignsMaintenance = () => (
        <ErrorPage autoPosition={true}
                   errorCode={999}/>
    )

    const maintenanceContentWrapper = {ContentWrapper: CampaignsMaintenance, BulkUploadWrapper: CampaignsMaintenance};

    const decideRouteHelperFunctions = {
        getCampaignsRoute,
        generateRoute,
        getUser,
        isAdvertiserLinked: util.isAdvertiserLinked,
        getPlatformDomains
    }

    return (
        <Provider store={store}>
            <ApolloProvider client={client}>
                <CampaignsProvider getUserLinkedAdvertiser={util.getUserLinkedAdvertiser}>
                    <UserContextProvider>
                        <DecideRoute components={components} MountPointWrapper={MountPointWrapper}
                                     PlatformApp={PlatformApp}
                                     maintenanceContentWrapper={maintenanceContentWrapper}
                                     helperFunctions={decideRouteHelperFunctions}
                                     UnauthorizedWrapper={UnauthorizedWrapper}
                                     LoadingWrapper={LoadingWrapper}/>
                    </UserContextProvider>
                </CampaignsProvider>
            </ApolloProvider>
        </Provider>
    );
}

export default CampaignsPlatformNavigation;
